import { Link } from "react-router-dom";
import { useSelectedOrganization } from "@/hooks/useSelectedOrganization";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import { Dropdown, DropdownTrigger, DropdownContent, DropdownItem, DropdownDivider } from "@/components/ui/Dropdown";
import type { OrgRole } from "@/types/api";

const ROLE_LABELS: Record<OrgRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Member",
  billing: "Billing",
};

const ROLE_VARIANTS: Record<OrgRole, "brand" | "warning" | "neutral" | "info"> = {
  owner: "brand",
  admin: "warning",
  member: "neutral",
  billing: "info",
};

function getInitials(name: string): string {
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export default function OrgSwitcher() {
  const {
    selectedOrganization,
    organizations,
    selectOrganization,
    isLoading,
    hasOrganizations,
  } = useSelectedOrganization();

  const handleOrgSelect = (orgId: string) => {
    selectOrganization(orgId);
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 py-2 opacity-70"
      >
        <Spinner size="sm" />
        <span className="text-sm text-text-quiet">
          Loading...
        </span>
      </div>
    );
  }

  // No organizations state
  if (!hasOrganizations) {
    return (
      <Link
        to="/dashboard/organizations/new"
        className="flex items-center gap-2 p-3 no-underline text-text bg-neutral-subtle rounded-lg border-2 border-dashed border-neutral"
      >
        <i className="fa-solid fa-plus text-brand" />
        <span className="font-medium">Create Organization</span>
      </Link>
    );
  }

  // Get role from the selected organization (now included in API response)
  const currentRole: OrgRole = selectedOrganization?.role || "member";

  return (
    <Dropdown>
      <DropdownTrigger asChild>
        <button
          className="w-full flex items-center gap-2 px-3 py-2 text-left border border-neutral rounded-lg bg-transparent hover:bg-surface-raised transition-colors cursor-pointer"
        >
          <div
            className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center font-semibold text-xs flex-shrink-0"
          >
            {getInitials(selectedOrganization?.name || "?")}
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="font-semibold overflow-hidden text-ellipsis whitespace-nowrap text-text"
            >
              {selectedOrganization?.name}
            </div>
            <div className="text-xs text-text-quiet">
              {ROLE_LABELS[currentRole]}
            </div>
          </div>
          <i className="fa-solid fa-chevron-down text-text-quiet text-xs" />
        </button>
      </DropdownTrigger>

      <DropdownContent align="start" className="min-w-[240px]">
        {/* Organization list */}
        {organizations.map((org) => {
          const isSelected = org.id === selectedOrganization?.id;
          const orgRole: OrgRole = org.role || "member";

          return (
            <DropdownItem
              key={org.id}
              onClick={() => handleOrgSelect(org.id)}
              className="flex items-center gap-2"
            >
              <div
                className="w-6 h-6 rounded-full bg-brand text-white flex items-center justify-center font-semibold text-[0.625rem] flex-shrink-0"
              >
                {getInitials(org.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className={`overflow-hidden text-ellipsis whitespace-nowrap ${isSelected ? "font-semibold" : ""}`}
                >
                  {org.name}
                </div>
              </div>
              <Badge
                variant={ROLE_VARIANTS[orgRole]}
                className="text-[0.625rem]"
              >
                {ROLE_LABELS[orgRole]}
              </Badge>
              {isSelected && (
                <i className="fa-solid fa-check text-brand text-xs" />
              )}
            </DropdownItem>
          );
        })}

        <DropdownDivider />

        {/* Create new organization */}
        <DropdownItem asChild>
          <Link
            to="/dashboard/organizations/new"
            className="flex items-center gap-2 no-underline text-inherit w-full"
          >
            <i className="fa-solid fa-plus" />
            Create Organization
          </Link>
        </DropdownItem>
      </DropdownContent>
    </Dropdown>
  );
}
